// Client mirror of server/helpers/classTiming.js. Kept deliberately in step so
// the weekly (undated) schedule views resolve the same effective class time the
// backend uses for dated term-timetable occurrences.
//
//   - "per_schedule": use the slot's own start/end time (default, legacy).
//   - "seasonal": summer/winter times, switched by the occurrence date.
//   - "per_term": every class in a term uses the term's start/end time.

export const TIMING_MODES = ["per_schedule", "seasonal", "per_term"];
export const DEFAULT_TIMING_MODE = "per_schedule";

export const DEFAULT_SEASONAL = {
  summer: { start: "11:00", end: "14:00" },
  winter: { start: "10:30", end: "13:30" },
  summerStart: "04-25",
  winterStart: "10-25",
};

export const TIMING_MODE_OPTIONS = [
  { label: "Per class", value: "per_schedule" },
  { label: "Summer / Winter", value: "seasonal" },
  { label: "Per term", value: "per_term" },
];

export const normaliseTimingMode = (m) =>
  TIMING_MODES.includes(m) ? m : DEFAULT_TIMING_MODE;

/** "9:5" / "09:05" / "09:05:00" -> "09:05:00"; junk -> null. */
export function normaliseTime(value) {
  if (value == null) return null;
  const m = String(value).trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  const s = Number(m[3] || 0);
  if (h > 23 || min > 59 || s > 59) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(min)}:${pad(s)}`;
}

function monthDayKey(value) {
  const m = String(value || "").trim().match(/^(\d{2})-(\d{2})$/);
  if (!m) return null;
  const mm = Number(m[1]);
  const dd = Number(m[2]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return mm * 100 + dd;
}

export function normaliseSeasonal(input = {}) {
  const src = input && typeof input === "object" ? input : {};
  const season = (s) => ({
    start: normaliseTime(s?.start),
    end: normaliseTime(s?.end),
  });
  return {
    summer: season(src.summer),
    winter: season(src.winter),
    summerStart:
      (monthDayKey(src.summerStart) && src.summerStart) || DEFAULT_SEASONAL.summerStart,
    winterStart:
      (monthDayKey(src.winterStart) && src.winterStart) || DEFAULT_SEASONAL.winterStart,
  };
}

export function normaliseTimingConfig(input = {}) {
  const src = input && typeof input === "object" ? input : {};
  return { seasonal: normaliseSeasonal(src.seasonal) };
}

/** Which season an ISO date falls in. @returns {"summer"|"winter"} */
export function seasonForDate(isoDate, seasonal = DEFAULT_SEASONAL) {
  const s = monthDayKey(seasonal.summerStart) ?? monthDayKey(DEFAULT_SEASONAL.summerStart);
  const w = monthDayKey(seasonal.winterStart) ?? monthDayKey(DEFAULT_SEASONAL.winterStart);
  const d = String(isoDate || "").slice(0, 10).match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!d) return "winter";
  const md = Number(d[1]) * 100 + Number(d[2]);
  if (s < w) return md >= s && md < w ? "summer" : "winter";
  return md >= w && md < s ? "winter" : "summer";
}

/**
 * Resolve the effective class time.
 * @returns {{start_time, end_time, source}}
 */
export function resolveClassTime({ mode, config, date, term, fallback = {} }) {
  const fb = {
    start_time: normaliseTime(fallback.start_time) || fallback.start_time || null,
    end_time: normaliseTime(fallback.end_time) || fallback.end_time || null,
  };
  const m = normaliseTimingMode(mode);

  if (m === "seasonal") {
    const seasonal = normaliseTimingConfig(config).seasonal;
    const which = seasonForDate(date, seasonal);
    const band = seasonal[which];
    if (band?.start && band?.end) {
      return { start_time: band.start, end_time: band.end, source: `seasonal:${which}` };
    }
    return { ...fb, source: "fallback" };
  }

  if (m === "per_term" && term) {
    const start = normaliseTime(term.start_time);
    const end = normaliseTime(term.end_time);
    if (start && end) return { start_time: start, end_time: end, source: "term" };
    return { ...fb, source: "fallback" };
  }

  return { ...fb, source: "schedule" };
}

/** Local YYYY-MM-DD (season lookups only care about month-day). */
function localTodayIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Effective times for a weekly, date-agnostic slot (schedule grids, parent
 * timetable). Seasonal resolves to TODAY's season; per_term falls back to the
 * slot's own time here (the dated term timetable is where per-term times apply).
 * @returns {{start_time, end_time, source}}
 */
export function effectiveSlotTimes(slot = {}, features = {}, opts = {}) {
  return resolveClassTime({
    mode: features.timingMode,
    config: features.timingConfig,
    date: opts.date || localTodayIso(),
    term: opts.term || null,
    fallback: { start_time: slot.start_time, end_time: slot.end_time },
  });
}

/** "13:30:00" -> "1:30 PM". Blank-safe. */
export function formatTime(t) {
  if (!t) return "";
  const [h, m] = String(t).split(":");
  const hour = Number(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}
